import React, {
  useState,
  useMemo,
  useEffect,
  ChangeEvent,
  useCallback,
} from 'react';
import { Checkbox, Divider, Tooltip } from 'antd';
// import { sectionMap } from './CustomAlertsFilterData';
import ToggleSwitch from '../../../components/ToggleSwitch';
import { AIstaricIcon } from '../../../assets/icons/icons';
import TruncateWithTooltip from '@/components/TruncateWithTooltip';
import EllipsisTooltip from '@/components/EllipsisTooltip';
import './CustomAlertThresholdFilter.css';

interface AppliedFilter {
  id: string;
  name?: string;
  title?: string;
}

interface AlertItem {
  id: string;
  title: string;
  icon?: boolean;
}

export interface AlertSection {
  type: string;
  label: string;
  toggleKey?: string;
  items?: AlertItem[];
  linkedIds?: string[];
  colorDot?: string;
}

interface ToggleStates {
  [key: string]: boolean;
  showCritical: boolean;
  showAtRisk: boolean;
}

export interface OnChangePayload {
  type: 'add' | 'remove' | 'bulk-add' | 'bulk-remove';
  item?: { id: string; name: string };
  items?: { id: string; name: string }[];
}

interface CustomAlertThresholdFilterProps {
  isHomePage?: boolean;
  selectedOptions?: AppliedFilter[];
  appliedFilters?: AppliedFilter[];
  onChange: (payload: OnChangePayload) => void;
  searchQuery?: string;
  resetSignal?: number;
  fullAlertFilterConfig?: AlertSection[];
  hiddenOptionIds?: Set<string>;
}
/**
 * Custom alert threshold filter component for filtering shipment alerts
 *
 * @param isHomePage - Whether component is rendered on the home page
 * @param selectedOptions - Array of currently selected filter options
 * @param appliedFilters - Array of applied filter objects
 * @param onChange - Callback function for filter changes
 * @param searchQuery - Search query string for filtering items
 * @param resetSignal - Signal to reset all toggles to active state
 */
const CustomAlertThresholdFilter: React.FC<CustomAlertThresholdFilterProps> = ({
  isHomePage = false,
  selectedOptions = [],
  appliedFilters = [],
  onChange,
  searchQuery = '',
  resetSignal,
  fullAlertFilterConfig,
  hiddenOptionIds = new Set(),
}) => {
  const [toggles, setToggles] = useState<ToggleStates>({
    showCritical: false,
    showAtRisk: false,
  });
  useEffect(() => {
    if (resetSignal) {
      const updatedToggles: Record<string, boolean> = {};
      fullAlertFilterConfig?.forEach((section: AlertSection) => {
        if (section.toggleKey) {
          updatedToggles[section.toggleKey] = true;
        }
      });
      setToggles((prev) => ({ ...prev, ...updatedToggles }));
    }
  }, [resetSignal, fullAlertFilterConfig]);

  const q = useMemo(() => searchQuery.trim().toLowerCase(), [searchQuery]);

  const isChecked = useCallback(
    (id: string): boolean => selectedOptions.some((f) => f.id === id),
    [selectedOptions]
  );

  useEffect(() => {
    if (!fullAlertFilterConfig?.length) return;

    const updatedToggles: Record<string, boolean> = {};

    fullAlertFilterConfig.forEach((section: AlertSection) => {
      if (!section.toggleKey) return;

      const allItems = Array.isArray(section.items) ? section.items : [];
      const idsToUse =
        section.linkedIds && section.linkedIds.length > 0
          ? section.linkedIds
          : allItems.map((i: AlertItem) => i.id);

      updatedToggles[section.toggleKey] =
        idsToUse.length > 0 && idsToUse.every((id: string) => isChecked(id));
    });

    setToggles((prev) => ({ ...prev, ...updatedToggles }));
  }, [selectedOptions, fullAlertFilterConfig, isChecked]);

  const handleToggle = (
    toggleKey: string,
    items: AlertItem[],
    linkedIds: string[] = []
  ): void => {
    const allItems =
      fullAlertFilterConfig?.flatMap(
        (section: AlertSection) => section.items || []
      ) ?? [];

    const idsToUse = (
      linkedIds.length ? linkedIds : items.map((i) => i.id)
    ).filter((id) => !hiddenOptionIds.has(String(id)));

    const updates = allItems
      .filter((item: AlertItem) => idsToUse.includes(item.id))
      .map((item: AlertItem) => ({ id: item.id, name: item.title }));

    const allSelected =
      idsToUse.length > 0 && idsToUse.every((id) => isChecked(id));

    if (allSelected) {
      onChange({ type: 'bulk-remove', items: updates });
    } else {
      onChange({ type: 'bulk-add', items: updates });
    }
  };

  const handleCheckboxChange = (
    e: ChangeEvent<HTMLInputElement> | { target: { checked: boolean } },
    item: AlertItem
  ): void => {
    const selected = { id: item.id, name: item.title };
    if (e.target.checked) onChange({ type: 'add', item: selected });
    else onChange({ type: 'remove', item: selected });
  };

  const itemMatches = (item: AlertItem): boolean =>
    !q || (item?.title || '').toLowerCase().includes(q);

  const renderItem = (item: AlertItem): React.JSX.Element => {
    const checked = isChecked(item.id);
    // Home uses the shared home-dd row, the same as the Home map filters.
    if (isHomePage) {
      return (
        <div key={item.id} className='home-dd__row'>
          <Checkbox
            checked={checked}
            onChange={(e) => handleCheckboxChange(e, item)}
            className='home-dd__check checkbox-sm'
          >
            <span className='home-dd__option'>
              <span className='home-dd__option-text'>
                <EllipsisTooltip text={item.title} className='home-dd__label' />
              </span>
              {item.icon && (
                <span className='home-alerts-staric-color'>
                  <AIstaricIcon />
                </span>
              )}
            </span>
          </Checkbox>
        </div>
      );
    }
    return (
      <div key={item.id} className='cat-filter__row'>
        <div className='cat-filter__row-main'>
          <Checkbox
            checked={checked}
            onChange={(e) => handleCheckboxChange(e, item)}
            className='custom-menu-shipment-checkbox-only checkbox-sm'
          />
          <TruncateWithTooltip
            text={item.title}
            max={30}
            render={(t) => (
              <span
                className='cat-filter__name'
                onClick={() =>
                  handleCheckboxChange({ target: { checked: !checked } }, item)
                }
              >
                {t}
              </span>
            )}
          />
        </div>

        {item.icon && (
          <span className={isHomePage ? 'home-alerts-staric-color' : ''}>
            <AIstaricIcon />
          </span>
        )}
      </div>
    );
  };

  // const getSectionMapKey = (toggleKey: string): string => {
  //   if (toggleKey === 'showCritical') return 'critical_alerts';
  //   if (toggleKey === 'showAtRisk') return 'at_risk_alerts';
  //   if (toggleKey === 'showAlertState') return 'alert_state';
  //   return '';
  // };

  const renderSection = (section: AlertSection): React.JSX.Element | null => {
    const isToggleControlled = section.toggleKey !== undefined;
    const toggleKey = section.toggleKey;

    const allItems = (Array.isArray(section.items) ? section.items : []).filter(
      (item) => !hiddenOptionIds.has(String(item.id))
    );

    const filteredItems = allItems.filter(itemMatches);
    if (q && filteredItems.length === 0) {
      return null;
    }

    const visibleIds =
      section.linkedIds && section.linkedIds.length > 0
        ? section.linkedIds.filter((id) => !hiddenOptionIds.has(String(id)))
        : allItems.map((i) => i.id);

    const disableToggle =
      isToggleControlled &&
      visibleIds.length > 0 &&
      visibleIds.every((id) =>
        appliedFilters.some((f) => String(f.id) === String(id))
      );

    const isToggleChecked = toggleKey ? !!toggles[toggleKey] : false;

    const isStrongSection = [
      'Alert state',
      'Critical alerts',
      'At-risk alerts',
    ].includes(section.label);

    const renderDividerBefore = section.label === 'Alert state';
    const renderDividerAfter =
      section.label === 'With AI' || section.label === 'Critical alerts';

    return (
      <div key={section.label} className='cat-filter__section'>
        {!isHomePage && (
          <>
            {renderDividerBefore && (
              <Divider
                style={{
                  margin: '12px 0',
                  padding: 0,
                  width: '105%',
                  left: '-11px',
                  position: 'relative',
                  borderColor: 'var(--gray-300)',
                }}
              />
            )}
          </>
        )}

        <div className='cat-filter__section-head'>
          <div className='cat-filter__section-title'>
            {section.label === 'Critical alerts' && (
              <span className='cat-filter__dot cat-filter__dot--critical' />
            )}
            {section.label === 'At-risk alerts' && (
              <span className='cat-filter__dot cat-filter__dot--at-risk' />
            )}
            <span
              className={`cat-filter__section-label${
                isStrongSection ? ' cat-filter__section-label--strong' : ''
              }`}
            >
              {section.label}
            </span>
          </div>

          {toggleKey !== undefined && (
            <Tooltip
              title={
                disableToggle
                  ? 'All related alerts applied — toggle disabled'
                  : ''
              }
            >
              <span className='cat-filter__toggle'>
                <ToggleSwitch
                  labelStyle='alert-customization-bg'
                  style={{ left: '-3px' }}
                  checked={isToggleChecked}
                  disabled={disableToggle}
                  onChange={() =>
                    handleToggle(toggleKey, allItems, section.linkedIds)
                  }
                />
              </span>
            </Tooltip>
          )}
        </div>

        {filteredItems.map((item: AlertItem) => renderItem(item))}

        {renderDividerAfter && (
          <Divider
            style={{
              margin: '12px 0',
              padding: 0,
              width: '105%',
              left: '-11px',
              position: 'relative',
              borderColor: 'var(--gray-300)',
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      {fullAlertFilterConfig?.map((section: AlertSection) =>
        renderSection(section)
      )}
    </div>
  );
};

export default CustomAlertThresholdFilter;
